import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KeyRound, User as UserIcon } from 'lucide-react';
import api from '../lib/axios';
import { isPasswordValid } from '../lib/password';
import { useAuthStore } from '../stores/authStore';
import CollapsibleCard from '../components/CollapsibleCard';

type Profile = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  provider: string;
  hasPassword: boolean;
  superAdmin: boolean;
};

export default function Profile() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const loadSession = useAuthStore((s) => s.loadSession);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameOk, setNameOk] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdOk, setPwdOk] = useState(false);

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/auth/profile').then((r) => r.data),
  });

  useEffect(() => {
    if (profile.data) {
      setFirstName(profile.data.firstName ?? '');
      setLastName(profile.data.lastName ?? '');
    }
  }, [profile.data]);

  const saveName = useMutation({
    mutationFn: () =>
      api
        .put<Profile>('/auth/profile', {
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
        })
        .then((r) => r.data),
    onSuccess: async () => {
      setNameError(null);
      setNameOk(true);
      qc.invalidateQueries({ queryKey: ['profile'] });
      await loadSession();
    },
    onError: (e: any) => {
      setNameOk(false);
      setNameError(e?.response?.data?.message ?? t('profile.saveError'));
    },
  });

  const savePassword = useMutation({
    mutationFn: () =>
      api.put('/auth/password', {
        currentPassword,
        newPassword,
      }),
    onSuccess: () => {
      setPwdError(null);
      setPwdOk(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (e: any) => {
      setPwdOk(false);
      setPwdError(e?.response?.data?.message ?? t('profile.passwordError'));
    },
  });

  const submitName = () => {
    setNameOk(false);
    setNameError(null);
    if (!firstName.trim() && !lastName.trim()) {
      setNameError(t('profile.nameRequired'));
      return;
    }
    saveName.mutate();
  };

  const submitPassword = () => {
    setPwdOk(false);
    setPwdError(null);
    if (!isPasswordValid(newPassword)) {
      setPwdError(t('profile.passwordPolicy'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError(t('profile.passwordMismatch'));
      return;
    }
    savePassword.mutate();
  };

  const data = profile.data;

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">{t('profile.eyebrow')}</p>
        <h1 className="page-title mt-1">{t('profile.title')}</h1>
      </header>

      {profile.isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}

      {data && (
        <>
          <CollapsibleCard
            className="mb-6 max-w-lg"
            title={
              <span className="flex items-center gap-2">
                <UserIcon size={18} className="text-ink-muted" /> {t('profile.identity')}
              </span>
            }
          >
            <div className="mb-4">
              <label className="field-label">{t('profile.email')}</label>
              <input value={data.email} disabled className="input bg-surface-raised" />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="field-label">{t('profile.firstName')}</label>
                <input
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setNameOk(false);
                  }}
                  className="input"
                  placeholder={t('profile.firstNamePlaceholder')}
                />
              </div>
              <div>
                <label className="field-label">{t('profile.lastName')}</label>
                <input
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setNameOk(false);
                  }}
                  className="input"
                  placeholder={t('profile.lastNamePlaceholder')}
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1">
                {nameError && <span className="text-sm font-medium text-expense">{nameError}</span>}
                {nameOk && <span className="text-sm font-medium text-income">{t('profile.saved')}</span>}
              </div>
              <button onClick={submitName} disabled={saveName.isPending} className="btn btn-primary">
                {saveName.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </CollapsibleCard>

          {data.hasPassword ? (
            <CollapsibleCard
              className="mb-6 max-w-lg"
              title={
                <span className="flex items-center gap-2">
                  <KeyRound size={18} className="text-ink-muted" /> {t('profile.password')}
                </span>
              }
            >
              <p className="mb-3 text-sm text-ink-muted">{t('profile.passwordPolicy')}</p>
              <div className="space-y-3">
                <div>
                  <label className="field-label">{t('profile.currentPassword')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setPwdOk(false);
                    }}
                    className="input"
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <label className="field-label">{t('profile.newPassword')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPwdOk(false);
                    }}
                    className="input"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="field-label">{t('profile.confirmPassword')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPwdOk(false);
                    }}
                    className="input"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1">
                  {pwdError && <span className="text-sm font-medium text-expense">{pwdError}</span>}
                  {pwdOk && <span className="text-sm font-medium text-income">{t('profile.passwordUpdated')}</span>}
                </div>
                <button onClick={submitPassword} disabled={savePassword.isPending} className="btn btn-primary">
                  {savePassword.isPending ? t('common.saving') : t('profile.changePassword')}
                </button>
              </div>
            </CollapsibleCard>
          ) : (
            <CollapsibleCard
              className="mb-6 max-w-lg"
              title={
                <span className="flex items-center gap-2">
                  <KeyRound size={18} className="text-ink-muted" /> {t('profile.password')}
                </span>
              }
            >
              <p className="text-sm text-ink-muted">{t('profile.googleOnly')}</p>
            </CollapsibleCard>
          )}
        </>
      )}
    </div>
  );
}
